/**
 * TODO(yang): refactor this component and FhirMRTTable to share code
 */

import { Menu } from '@mantine/core'
import type { SearchRequest } from '@medplum/core'
import type {
  ExtractResource,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { searchRequestAsObject } from '@seen/core/display'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
  ResourceOf,
} from '@seen/core/ExtendedResource'
import type { QueryTypes } from '@seen/core/search'
import type { MRT_RowData, MRT_TableOptions } from 'mantine-react-table'
import { type JSX, useMemo } from 'react'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { ReactTable } from '#src/components/fhir-table/ReactTable.tsx'
import { ReactTableDeleteAction } from '#src/components/fhir-table/ReactTableDeleteAction.tsx'
import { MantineMenuItemMedplumLinkAction } from '#src/components/fhir-table/ReactTableMedplumLinkAction.tsx'
import { useFhirSearch } from '#src/hooks/useFhirSearch.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

// Define TableOptions to be generic over R
type TableOptions<R extends MRT_RowData> = Omit<
  MRT_TableOptions<R>,
  'columns' | 'data'
>

// Update the props interface to include generic R
interface ExtendedResourceTableProps<
  T extends ResourceType,
  R extends ExtendedResource<ExtractResource<T>>,
> {
  resourceClass: ExtendedResourceConstructor<R>
  columnConfigs: ColumnConfig<R>[]
  filterQuery?: QueryTypes
  searchRequest?: SearchRequest
  tableOptions?: TableOptions<R>
  showAdminActions?: boolean
}

export function ExtendedResourceTable<
  T extends ResourceType,
  R extends ExtendedResource<ExtractResource<T>>,
>({
  resourceClass,
  columnConfigs,
  filterQuery,
  searchRequest,
  tableOptions,
  showAdminActions: showAdminActionsOverride,
}: ExtendedResourceTableProps<T, R>): JSX.Element {
  const showAdminActionsDefault = usePortalStore(
    (store) => store.showAdminActions
  )
  const showAdminActions = showAdminActionsOverride ?? showAdminActionsDefault

  const resourceType = resourceClass.RESOURCE_TYPE
  const search = useMemo<SearchRequest>(
    () => ({
      resourceType,
      total: searchRequest?.total ?? ('accurate' as const),
      sortRules: [
        {
          code: '_id',
          descending: true,
        },
      ],
      ...searchRequest,
    }),
    [searchRequest, resourceType]
  )

  const tableOptionsWithDefaults: TableOptions<R> = {
    positionActionsColumn: 'last',
    enableRowActions: showAdminActions,
    renderRowActionMenuItems: (props) => {
      const original = props.row.original
      const row = original.toFhirResource()
      return (
        <>
          <Menu.Label>Admin</Menu.Label>
          <MantineMenuItemMedplumLinkAction resource={row} />
          {/* TODO(yang): remove this once we migrate to model layer for all resources */}
          <ReactTableDeleteAction row={row as Resource} />
        </>
      )
    },
    getRowId: (row) => row.id,
    ...tableOptions,
  }

  const searchQuery = useFhirSearch(resourceType, {
    ...searchRequestAsObject(search),
    ...filterQuery,
  })

  return (
    <FhirQueryLoader query={searchQuery}>
      {(data) => (
        <ReactTable<R>
          columnConfigs={columnConfigs}
          data={data.map((d) =>
            resourceClass.fromFhirResource(d as ResourceOf<R>)
          )}
          tableOptions={tableOptionsWithDefaults}
        />
      )}
    </FhirQueryLoader>
  )
}
